import React, { useState, useEffect, useCallback, useContext } from 'react';
import withAuth from '../../common/auth';
import { ToastContainer, toast } from 'react-toastify';
import { Slide } from 'react-toastify';
import { FiRefreshCcw, FiDownloadCloud, FiX, FiFileText, FiFile } from "react-icons/fi";
import { GET_PURCHASE_REPORT_WITH_ITEMS, BASE_URL } from '../../utlilities/config';
import axios from 'axios';
import { initializeWebUserInfo, getWebUserInfo } from '../../utlilities/webConfig';
import { TabContext } from '../../components/TabbedLayout';
import { ReportEmbedContext } from '../reportEmbedContext';
import * as XLSX from 'xlsx';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import HighlightMetricsModal from './components/HighlightMetricsModal';

// Enhanced Spinner Component
const Spinner = () => (
    <div className="fixed inset-0 z-[60] flex items-center justify-center bg-white bg-opacity-80 backdrop-blur-sm">
      <div className="flex flex-col items-center gap-3">
        <div className="animate-spin rounded-full h-16 w-16 border-t-4 border-b-4 border-indigo-600"></div>
        <p className="text-indigo-600 font-medium">Loading...</p>
      </div>
    </div>
);

// Helper function to format date in local timezone (IST)
const formatLocalDate = (date) => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

// Build initial filters
const buildInitialFilters = (filters = []) => {
  const today = new Date();
  const todayFormatted = formatLocalDate(today);

  return filters.reduce((accumulator, filter) => {
    if (filter.type === 'date') {
      const dateValue = todayFormatted;
      return { ...accumulator, [filter.id]: dateValue };
    }

    if (filter.type === 'select') {
      const defaultValue = filter.options?.[0]?.value ?? '';
      return { ...accumulator, [filter.id]: defaultValue };
    }

    return { ...accumulator, [filter.id]: '' };
  }, {});
};

// Report configuration
const reportConfig = {
  filters: [
    { id: 'fromDate', type: 'date', label: 'From Date' },
    { id: 'toDate', type: 'date', label: 'To Date' },
  ],
  table: {
    columns: [
      { key: 'Sl', label: 'Sl.' },
      { key: 'BillNo', label: 'Bill No' },
      { key: 'BillDate', label: 'Bill Date' },
      { key: 'Supplier', label: 'Supplier' },
      { key: 'ProductName', label: 'Product Name' },
      { key: 'Batch', label: 'Batch' },
      { key: 'Nos', label: 'Nos' },
      { key: 'Rate', label: 'Rate' },
      { key: 'TaxPercent', label: 'Tax %' },
      { key: 'GTotal', label: 'G.Total' },
      { key: 'Tax', label: 'Tax' },
      { key: 'CGST', label: 'CGST' },
      { key: 'SGST', label: 'SGST' },
      { key: 'Amount', label: 'Amount' },
      { key: 'DisPercent', label: 'Dis %' },
      { key: 'Discount', label: 'Discount' },
    ],
  },
};

const REPORT_FETCH_PAGE_SIZE = 100000000;
const FRONTEND_ITEMS_PER_PAGE = 500;

const formatApiDate = (date) => {
    if (!date) return '';
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
};

const transformPurchaseRows = (rows = []) => {
    let previousBillNo = null;
    return rows.map((item) => {
        const isFirstItem = previousBillNo !== item.InvoiceNo;
        previousBillNo = item.InvoiceNo;

        let discountAmount = parseFloat(item.DiscountAmount || 0) || 0;
        const discountPercentage = parseFloat(item.DiscountPercentage || 0) || 0;
        const gTotal = parseFloat(item.Total || 0) || 0;

        if (discountPercentage > 0 && discountAmount === 0) {
            discountAmount = (gTotal * discountPercentage) / 100;
        }

        return {
            BillNo: isFirstItem ? item.InvoiceNo : '',
            BillDate: isFirstItem ? item.InvoiceDate : '',
            Supplier: isFirstItem ? item.SupplierName : '',
            ProductName: item.ItemName,
            Batch: item.BatchCode,
            Nos: item.Quantity,
            Rate: item.PurchasePrice,
            TaxPercent: item.TaxCode,
            TRate: item.TaxAmount,
            GTotal: item.Total,
            Taxablevalue: item.Taxablevalue,
            Tax: item.TaxAmount,
            CGST: item.CGST,
            SGST: item.SGST,
            Amount: item.InvoiceValue,
            DisPercent: discountPercentage,
            Discount: discountAmount,
        };
    });
};

// Export Format Selection Modal
const ExportFormatModal = ({ isOpen, onClose, onSelectFormat }) => {
    if (!isOpen) return null;

    return (
        <div className="fixed inset-0 z-[1100] flex items-center justify-center bg-black bg-opacity-60 backdrop-blur-sm">
            <div className="bg-white rounded-2xl shadow-2xl w-96 max-w-md transform transition-transform duration-300 ease-out">
                <div className="bg-gradient-to-r from-indigo-600 to-purple-600 px-6 py-4 flex justify-between items-center rounded-t-2xl">
                    <h2 className="text-xl font-semibold text-white flex items-center">
                        <FiDownloadCloud className="mr-2" /> Export Report
                    </h2>
                    <button
                        onClick={onClose}
                        className="text-white hover:bg-white hover:bg-opacity-20 rounded-full p-1 transition-colors duration-200"
                        aria-label="Close modal"
                    >
                        <FiX size={20} />
                    </button>
                </div>
                <div className="p-6">
                    <p className="text-sm text-slate-600 mb-4">Choose export format:</p>
                    <div className="flex flex-col gap-3">
                        <button
                            onClick={() => onSelectFormat('excel')}
                            className="flex items-center gap-3 p-4 rounded-lg border-2 border-slate-200 hover:border-indigo-500 hover:bg-indigo-50 transition-all duration-200 group"
                        >
                            <div className="w-10 h-10 rounded-lg bg-green-100 flex items-center justify-center group-hover:bg-green-200 transition-colors">
                                <FiFileText className="text-green-600" size={20} />
                            </div>
                            <div className="flex-1 text-left">
                                <p className="font-semibold text-slate-800">Export to Excel</p>
                                <p className="text-xs text-slate-500">Download as .xlsx file</p>
                            </div>
                        </button>
                        <button
                            onClick={() => onSelectFormat('pdf')}
                            className="flex items-center gap-3 p-4 rounded-lg border-2 border-slate-200 hover:border-indigo-500 hover:bg-indigo-50 transition-all duration-200 group"
                        >
                            <div className="w-10 h-10 rounded-lg bg-red-100 flex items-center justify-center group-hover:bg-red-200 transition-colors">
                                <FiFile className="text-red-600" size={20} />
                            </div>
                            <div className="flex-1 text-left">
                                <p className="font-semibold text-slate-800">Export to PDF</p>
                                <p className="text-xs text-slate-500">Download as .pdf file</p>
                            </div>
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
};

export const PurchaseLedger = () => {
    const tabContext = useContext(TabContext);
    const embedded = useContext(ReportEmbedContext);
    const [loading, setLoading] = useState(false);
    const [filters, setFilters] = useState(() => buildInitialFilters(reportConfig.filters));
    const [isMetricsModalOpen, setIsMetricsModalOpen] = useState(false);
    const [purchaseData, setPurchaseData] = useState([]);
    const [allPurchaseData, setAllPurchaseData] = useState([]);
    const [lastRefreshedAt, setLastRefreshedAt] = useState(null);
    const token = localStorage.getItem('accessToken');
    const [userInfo, setUserInfo] = useState({});
    const [showExportModal, setShowExportModal] = useState(false);
    
    // Pagination state
    const [currentPage, setCurrentPage] = useState(1);
    const [pageContext, setPageContext] = useState({
        PageNo: 1,
        PageSize: FRONTEND_ITEMS_PER_PAGE,
        TotalRecords: 0,
        TotalPages: 0,
        HasNextPage: false,
        HasPreviousPage: false
    });

    const isElectron = () => {
        return window && window.process && window.process.type;
    };
    
    let ipcRenderer;

    if (isElectron()) {
        ipcRenderer = window.require('electron').ipcRenderer;
    }

    useEffect(() => {
        const fetchData = async () => {
            let userinfo;

            if (isElectron()) {
                userinfo = await ipcRenderer.invoke('store-get', 'userInfo');
                setUserInfo(userinfo);
            } else {
                initializeWebUserInfo();
                userinfo = getWebUserInfo().userInfo;
                setUserInfo(userinfo);
            }
        };

        fetchData();
    }, []);
    

    const handleFilterChange = (filterId, value) => {
        setFilters((prev) => ({
            ...prev,
            [filterId]: value,
        }));
    };

    const handleRunReport = () => {
        setCurrentPage(1);
        getPurchaseReport();
    };

    const handleExport = () => {
        // Check if filters are set
        if (!filters.fromDate || !filters.toDate) {
            toast.error('Please select date range before exporting', { autoClose: 2000 });
            return;
        }
        setShowExportModal(true);
    };

    const exportToExcel = async () => {
        setLoading(true);
        try {
            if (allPurchaseData.length === 0) {
                toast.error('Please generate the report before exporting', { autoClose: 2000 });
                return;
            }

            const transformedData = allPurchaseData;

            const toNumericCell = (value, decimals = 2) => {
                const parsed = parseFloat(value ?? 0);
                if (!Number.isFinite(parsed)) return 0;
                return Number(parsed.toFixed(decimals));
            };

            const toDateCell = (value) => {
                if (!value) return '';
                const parsedDate = new Date(value);
                return Number.isNaN(parsedDate.getTime()) ? '' : parsedDate;
            };

            const excelData = transformedData.map((row, index) => ({
                'Sl.': index + 1,
                'Bill No': row.BillNo || '',
                'Bill Date': toDateCell(row.BillDate),
                'Supplier': row.Supplier || '',
                'Product Name': row.ProductName || '',
                'Batch': row.Batch || '',
                'Nos': toNumericCell(row.Nos, 3),
                'Rate': toNumericCell(row.Rate, 2),
                'Tax %': toNumericCell(row.TaxPercent, 2),
                'T.Rate': toNumericCell(row.TRate, 2),
                'G.Total': toNumericCell(row.Taxablevalue, 2),
                'Tax': toNumericCell(row.Tax, 2),
                'CGST': toNumericCell(row.CGST, 2),
                'SGST': toNumericCell(row.SGST, 2),
                'Amount': toNumericCell(row.Amount, 2),
                'Dis %': toNumericCell(row.DisPercent, 2),
                'Discount': toNumericCell(row.Discount, 2),
            }));

            const wb = XLSX.utils.book_new();
            const ws = XLSX.utils.json_to_sheet(excelData, {
                cellDates: true,
                dateNF: 'dd/mm/yyyy',
            });

            const colWidths = [
                { wch: 6 },   // Sl.
                { wch: 10 },  // Bill No
                { wch: 12 },  // Bill Date
                { wch: 30 },  // Supplier
                { wch: 30 },  // Product Name
                { wch: 12 },  // Batch
                { wch: 10 },  // Nos
                { wch: 10 },  // Rate
                { wch: 8 },   // Tax %
                { wch: 10 },  // T.Rate
                { wch: 12 },  // G.Total
                { wch: 10 },  // Tax
                { wch: 10 },  // CGST
                { wch: 10 },  // SGST
                { wch: 12 },  // Amount
                { wch: 8 },   // Dis %
                { wch: 12 },  // Discount
            ];
            ws['!cols'] = colWidths;

            // Apply Excel number/date display formats while keeping underlying numeric/date types.
            const dataRowStart = 2; // Row 1 is header in sheet
            const dataRowEnd = excelData.length + 1;
            for (let rowIndex = dataRowStart; rowIndex <= dataRowEnd; rowIndex += 1) {
                const billDateCell = ws[`C${rowIndex}`];
                if (billDateCell && billDateCell.v) {
                    billDateCell.z = 'dd/mm/yyyy';
                }

                const nosCell = ws[`G${rowIndex}`];
                if (nosCell) nosCell.z = '0.000';

                const twoDecimalCols = ['H', 'I', 'J', 'K', 'L', 'M', 'N', 'O', 'P', 'Q'];
                twoDecimalCols.forEach((col) => {
                    const cell = ws[`${col}${rowIndex}`];
                    if (cell) cell.z = '0.00';
                });
            }

            XLSX.utils.book_append_sheet(wb, ws, 'Purchase Ledger');

            const fromDateStr = filters.fromDate ? new Date(filters.fromDate).toLocaleDateString('en-GB').replace(/\//g, '-') : 'All';
            const toDateStr = filters.toDate ? new Date(filters.toDate).toLocaleDateString('en-GB').replace(/\//g, '-') : 'All';
            const filename = `Purchase_Ledger_${fromDateStr}_to_${toDateStr}.xlsx`;

            XLSX.writeFile(wb, filename);
            toast.success(`Excel file exported successfully with ${transformedData.length} records`, { autoClose: 3000 });
            setShowExportModal(false);
        } catch (error) {
            console.error('Error exporting to Excel:', error);
            toast.error('Failed to export to Excel', { autoClose: 2000 });
        } finally {
            setLoading(false);
        }
    };

    const exportToPDF = async () => {
        setLoading(true);
        try {
            if (allPurchaseData.length === 0) {
                toast.error('Please generate the report before exporting', { autoClose: 2000 });
                return;
            }

            const transformedData = allPurchaseData;

            const doc = new jsPDF('landscape', 'mm', 'a4');
            
            doc.setFontSize(16);
            doc.text('Purchase Ledger Report', 14, 15);
            
            doc.setFontSize(10);
            const fromDateStr = filters.fromDate ? new Date(filters.fromDate).toLocaleDateString('en-GB') : 'All';
            const toDateStr = filters.toDate ? new Date(filters.toDate).toLocaleDateString('en-GB') : 'All';
            doc.text(`Date Range: ${fromDateStr} to ${toDateStr}`, 14, 22);
            
            const tableData = transformedData.map((row, index) => [
                index + 1,
                row.BillNo || '',
                row.BillDate ? new Date(row.BillDate).toLocaleDateString('en-GB') : '',
                row.Supplier || '',
                row.ProductName || '',
                row.Batch || '',
                parseFloat(row.Nos || 0).toFixed(3),
                parseFloat(row.Rate || 0).toFixed(2),
                parseFloat(row.TaxPercent || 0).toFixed(2),
                parseFloat(row.TRate || 0).toFixed(2),
                parseFloat(row.GTotal || 0).toFixed(2),
                parseFloat(row.Tax || 0).toFixed(2),
                parseFloat(row.CGST || 0).toFixed(2),
                parseFloat(row.SGST || 0).toFixed(2),
                parseFloat(row.Amount || 0).toFixed(2),
                parseFloat(row.DisPercent || 0).toFixed(2),
                parseFloat(row.Discount || 0).toFixed(2),
            ]);

            autoTable(doc, {
                head: [['Sl.', 'Bill No', 'Bill Date', 'Supplier', 'Product Name', 'Batch', 'Nos', 'Rate', 'Tax %', 'T.Rate', 'G.Total', 'Tax', 'CGST', 'SGST', 'Amount', 'Dis %', 'Discount']],
                body: tableData,
                startY: 28,
                styles: { fontSize: 6, cellPadding: 1 },
                headStyles: { fillColor: [99, 102, 241], textColor: 255, fontStyle: 'bold' },
                alternateRowStyles: { fillColor: [249, 250, 251] },
                margin: { top: 28, left: 14, right: 14 },
            });

            const filename = `Purchase_Ledger_${fromDateStr.replace(/\//g, '-')}_to_${toDateStr.replace(/\//g, '-')}.pdf`;
            doc.save(filename);
            toast.success(`PDF file exported successfully with ${transformedData.length} records`, { autoClose: 3000 });
            setShowExportModal(false);
        } catch (error) {
            console.error('Error exporting to PDF:', error);
            toast.error('Failed to export to PDF', { autoClose: 2000 });
        } finally {
            setLoading(false);
        }
    };

    const handleExportFormat = (format) => {
        if (format === 'excel') {
            exportToExcel();
        } else if (format === 'pdf') {
            exportToPDF();
        }
    };

    const itemsPerPage = FRONTEND_ITEMS_PER_PAGE;

    // Pagination calculations - frontend pagination
    const totalPages = pageContext.TotalPages || 0;
    const totalRecords = pageContext.TotalRecords || 0;
    const startIndex = (currentPage - 1) * itemsPerPage;
    const endIndex = Math.min(startIndex + purchaseData.length, totalRecords);
    const displayStart = totalRecords === 0 ? 0 : startIndex + 1;

    // Reset to page 1 when filters change
    useEffect(() => {
        setCurrentPage(1);
    }, [filters.fromDate, filters.toDate]);

    const getPurchaseReport = useCallback(async () => {
        setLoading(true);
        try {
            const fromDate = filters.fromDate ? new Date(filters.fromDate) : null;
            const toDate = filters.toDate ? new Date(filters.toDate) : null;
            
            const response = await axios.get(`${BASE_URL}${GET_PURCHASE_REPORT_WITH_ITEMS}`, {
                headers: {
                    'XApiKey': process.env.REACT_APP_API_KEY,
                    'Authorization': `Bearer ${token}`,
                },
                params: {
                    'fromDate': formatApiDate(fromDate),
                    'toDate': formatApiDate(toDate),
                    'pageNo': 1,
                    'pageSize': REPORT_FETCH_PAGE_SIZE,
                },
            });
            
            if (response.status === 200 && response.data.isSucess) {
                const data = response.data.data?.purchaseData || [];
                
                // Filter only Pharmacy items
                const pharmacyData = data.filter(item => item.ItemType === 'Pharmacy');

                const transformedData = transformPurchaseRows(pharmacyData);
                setAllPurchaseData(transformedData);
                setLastRefreshedAt(new Date());
            } else {
                toast.error('Failed to fetch purchase report', { autoClose: 1000 });
            }
        } catch (error) {
            console.error('Failed to fetch purchase report:', error);
            toast.error('Failed to fetch purchase report', { autoClose: 1000 });
        } finally {
            setLoading(false);
        }
    }, [filters, token]);

    useEffect(() => {
        const frontendTotalPages = Math.ceil(allPurchaseData.length / itemsPerPage);
        if (frontendTotalPages > 0 && currentPage > frontendTotalPages) {
            setCurrentPage(frontendTotalPages);
            return;
        }

        const paginatedData = allPurchaseData.slice(
            (currentPage - 1) * itemsPerPage,
            currentPage * itemsPerPage
        );

        setPurchaseData(paginatedData);
        setPageContext({
            PageNo: currentPage,
            PageSize: itemsPerPage,
            TotalRecords: allPurchaseData.length,
            TotalPages: frontendTotalPages,
            HasNextPage: currentPage < frontendTotalPages,
            HasPreviousPage: currentPage > 1
        });
    }, [allPurchaseData, currentPage, itemsPerPage]);

    return (
        <div className="flex h-full bg-background">
            <ToastContainer
                position="top-center"
                autoClose={1000}
                limit={1}
                hideProgressBar={false}
                newestOnTop={false}
                closeOnClick
                rtl={false}
                pauseOnFocusLoss
                draggable
                pauseOnHover
                theme="light"
                transition={Slide}
            />
            
            {loading && <Spinner />}

            <ExportFormatModal
                isOpen={showExportModal}
                onClose={() => setShowExportModal(false)}
                onSelectFormat={handleExportFormat}
            />

            <div className="flex-1 flex flex-col h-full overflow-hidden">
                <header className="shrink-0 border-b border-slate-200 bg-white px-3 py-2.5 mb-2">
                    <div className="flex flex-col gap-3 lg:flex-row lg:items-end lg:justify-between">
                        <div className="min-w-0 flex-1">
                            {!embedded && (
                                <div className="mb-2">
                                    <p className="text-xs font-semibold uppercase tracking-widest text-indigo-500">
                                        Purchase Report
                                    </p>
                                    <h1 className="text-lg font-bold text-slate-800">
                                        Purchase Ledger
                                    </h1>
                                </div>
                            )}
                            <div className="flex flex-col gap-3 sm:flex-row sm:items-end sm:gap-3">
                                {reportConfig.filters.map((filter) => {
                                    if (filter.type === 'date') {
                                        return (
                                            <label key={filter.id} className="flex flex-col gap-1 text-xs font-medium text-slate-700 shrink-0 w-[9.5rem]">
                                                {filter.label}
                                                <input
                                                    type="date"
                                                    value={filters[filter.id] ?? ''}
                                                    min={filter.id === 'toDate' ? filters.fromDate : undefined}
                                                    onChange={(event) => handleFilterChange(filter.id, event.target.value)}
                                                    className="w-full rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm text-slate-700 shadow-sm focus:border-indigo-400 focus:ring-2 focus:ring-indigo-200 transition"
                                                />
                                            </label>
                                        );
                                    }
                                    return null;
                                })}
                            </div>
                        </div>
                        <div className="flex flex-wrap items-center justify-start lg:justify-end gap-2 sm:gap-3">
                            <button
                                type="button"
                                onClick={handleRunReport}
                                className="inline-flex items-center gap-2 rounded-full bg-indigo-500 px-5 py-2 text-sm font-semibold text-white shadow-sm transition hover:bg-indigo-600"
                            >
                                <FiRefreshCcw size={16} />
                                Generate
                            </button>
                            <button
                                type="button"
                                onClick={() => {
                                    setFilters(buildInitialFilters(reportConfig.filters));
                                }}
                                className="inline-flex items-center gap-2 rounded-full border border-slate-200 px-5 py-2 text-sm font-semibold text-slate-600 shadow-sm transition hover:border-indigo-300 hover:text-indigo-500"
                            >
                                Reset
                            </button>
                            <button
                                type="button"
                                onClick={handleExport}
                                className="inline-flex items-center gap-2 rounded-full border border-slate-200 px-5 py-2 text-sm font-semibold text-slate-600 shadow-sm transition hover:border-indigo-300 hover:text-indigo-500"
                            >
                                <FiDownloadCloud size={16} />
                                Export
                            </button>
                        </div>
                    </div>
                </header>

                <main className="flex-1 overflow-hidden px-2 py-2 flex flex-col">
                    <div className="flex flex-col gap-3 flex-1 min-h-0">
                        <HighlightMetricsModal
                            isOpen={isMetricsModalOpen}
                            onOpen={() => setIsMetricsModalOpen(true)}
                            onClose={() => setIsMetricsModalOpen(false)}
                        >
                            <div className="grid gap-4 md:grid-cols-5">
                                <div className="rounded-md border border-indigo-100 bg-indigo-50/60 p-2 text-slate-700 shadow-sm">
                                    <p className="text-xs font-semibold uppercase tracking-wide text-indigo-500">
                                        Total Items
                                    </p>
                                    <p className="mt-1 text-md font-semibold text-indigo-600">{totalRecords}</p>
                                </div>
                                <div className="rounded-xl border border-indigo-100 bg-indigo-50/60 p-2 text-slate-700 shadow-sm">
                                    <p className="text-xs font-semibold uppercase tracking-wide text-indigo-500">
                                        Total Amount
                                    </p>
                                    <p className="mt-1 text-md font-semibold text-indigo-600">
                                        ₹{allPurchaseData.reduce((sum, item) => sum + (parseFloat(item.Amount || 0)), 0).toFixed(2)}
                                    </p>
                                </div>
                                <div className="rounded-xl border border-indigo-100 bg-indigo-50/60 p-2 text-slate-700 shadow-sm">
                                    <p className="text-xs font-semibold uppercase tracking-wide text-indigo-500">
                                        Total Discount
                                    </p>
                                    <p className="mt-1 text-md font-semibold text-indigo-600">
                                        ₹{allPurchaseData.reduce((sum, item) => sum + (parseFloat(item.Discount || 0)), 0).toFixed(2)}
                                    </p>
                                </div>
                                <div className="rounded-xl border border-indigo-100 bg-indigo-50/60 p-2 text-slate-700 shadow-sm">
                                    <p className="text-xs font-semibold uppercase tracking-wide text-indigo-500">
                                        Total Tax
                                    </p>
                                    <p className="mt-1 text-md font-semibold text-indigo-600">
                                        ₹{allPurchaseData.reduce((sum, item) => sum + (parseFloat(item.Tax || 0)), 0).toFixed(2)}
                                    </p>
                                </div>
                                <div className="rounded-xl border border-indigo-100 bg-indigo-50/60 p-2 text-slate-700 shadow-sm">
                                    <p className="text-xs font-semibold uppercase tracking-wide text-indigo-500">
                                        Grand Total
                                    </p>
                                    <p className="mt-1 text-md font-semibold text-indigo-600">
                                        ₹{allPurchaseData.reduce((sum, item) => sum + (parseFloat(item.GTotal || 0)), 0).toFixed(2)}
                                    </p>
                                </div>
                            </div>
                        </HighlightMetricsModal>

                        {/* Table Section */}
                        <section className="rounded-2xl border border-slate-200 bg-white p-3 shadow-sm flex flex-col flex-1 min-h-0">
                            <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between flex-shrink-0 pb-1">
                                <div className="flex flex-col gap-2">
                                    <p className="text-sm text-slate-500">
                                        Showing <span className="font-semibold text-slate-700">{displayStart}-{endIndex}</span> of <span className="font-semibold text-slate-700">{totalRecords}</span> items for this report.
                                    </p>
                                </div>
                                {/* Pagination Controls */}
                                {totalPages > 1 && (
                                    <div className="flex items-center gap-2">
                                        <button
                                            onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
                                            disabled={!pageContext.HasPreviousPage || currentPage === 1}
                                            className="px-3 py-1.5 rounded-lg border border-slate-200 bg-white text-sm font-medium text-slate-700 disabled:opacity-50 disabled:cursor-not-allowed hover:bg-indigo-50 hover:border-indigo-300 transition-colors"
                                        >
                                            Previous
                                        </button>
                                        <div className="flex items-center gap-1">
                                            {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                                                let pageNum;
                                                if (totalPages <= 5) {
                                                    pageNum = i + 1;
                                                } else if (currentPage <= 3) {
                                                    pageNum = i + 1;
                                                } else if (currentPage >= totalPages - 2) {
                                                    pageNum = totalPages - 4 + i;
                                                } else {
                                                    pageNum = currentPage - 2 + i;
                                                }
                                                return (
                                                    <button
                                                        key={pageNum}
                                                        onClick={() => setCurrentPage(pageNum)}
                                                        className={`px-3 py-1.5 rounded-lg border text-sm font-medium transition-colors ${
                                                            currentPage === pageNum
                                                                ? 'bg-indigo-600 text-white border-indigo-600'
                                                                : 'border-slate-200 bg-white text-slate-700 hover:bg-indigo-50 hover:border-indigo-300'
                                                        }`}
                                                    >
                                                        {pageNum}
                                                    </button>
                                                );
                                            })}
                                        </div>
                                        <span className="text-sm text-slate-600">
                                            Page {currentPage} of {totalPages}
                                        </span>
                                        <button
                                            onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}
                                            disabled={!pageContext.HasNextPage || currentPage === totalPages}
                                            className="px-3 py-1.5 rounded-lg border border-slate-200 bg-white text-sm font-medium text-slate-700 disabled:opacity-50 disabled:cursor-not-allowed hover:bg-indigo-50 hover:border-indigo-300 transition-colors"
                                        >
                                            Next
                                        </button>
                                    </div>
                                )}
                            </div>
                            <div className="mt-2 flex-1 overflow-auto min-h-0 rounded-xl border border-slate-200">
                                <table className="min-w-full divide-y divide-slate-200 text-sm text-slate-700">
                                    <thead className="bg-gray-200 sticky top-0 z-10">
                                        <tr>
                                            {reportConfig.table.columns.map((column) => (
                                                <th
                                                    key={column.key}
                                                    scope="col"
                                                    className="px-2 py-2 text-left text-xs font-semibold uppercase tracking-wide text-slate-500 whitespace-nowrap"
                                                >
                                                    {column.label}
                                                </th>
                                            ))}
                                        </tr>
                                    </thead>
                                    <tbody className="divide-y divide-slate-100 bg-white">
                                        {purchaseData.length > 0 ? (
                                            purchaseData.map((row, index) => (
                                                <tr key={startIndex + index} className="hover:bg-indigo-50/30">
                                                    <td className="px-2 py-2 text-slate-700 whitespace-nowrap">{startIndex + index + 1}</td>
                                                    <td className="px-2 py-2 text-slate-700 whitespace-nowrap">{row.BillNo || ''}</td>
                                                    <td className="px-2 py-2 text-slate-700 whitespace-nowrap">
                                                        {row.BillDate ? new Date(row.BillDate).toLocaleDateString('en-GB', {
                                                            day: '2-digit',
                                                            month: '2-digit',
                                                            year: 'numeric'
                                                        }) : ''}
                                                    </td>
                                                    <td className="px-2 py-2 text-slate-700 whitespace-nowrap">{row.Supplier || ''}</td>
                                                    <td className="px-2 py-2 text-slate-700 whitespace-nowrap">{row.ProductName || ''}</td>
                                                    <td className="px-2 py-2 text-slate-700 whitespace-nowrap">{row.Batch || ''}</td>
                                                    <td className="px-2 py-2 text-slate-700 whitespace-nowrap text-left">{parseFloat(row.Nos || 0).toFixed(3)}</td>
                                                    <td className="px-2 py-2 text-slate-700 whitespace-nowrap text-left">₹{parseFloat(row.Rate || 0).toFixed(2)}</td>
                                                    <td className="px-2 py-2 text-slate-700 whitespace-nowrap text-left">{parseFloat(row.TaxPercent || 0).toFixed(2)}</td>
                                                    <td className="px-2 py-2 text-slate-700 whitespace-nowrap text-left">₹{parseFloat(row.Taxablevalue || 0).toFixed(2)}</td>
                                                    <td className="px-2 py-2 text-slate-700 whitespace-nowrap text-left">₹{parseFloat(row.Tax || 0).toFixed(2)}</td>
                                                    <td className="px-2 py-2 text-slate-700 whitespace-nowrap text-left">₹{parseFloat(row.CGST || 0).toFixed(2)}</td>
                                                    <td className="px-2 py-2 text-slate-700 whitespace-nowrap text-left">₹{parseFloat(row.SGST || 0).toFixed(2)}</td>
                                                    <td className="px-2 py-2 text-slate-700 whitespace-nowrap text-left">₹{parseFloat(row.Amount || 0).toFixed(2)}</td>
                                                    <td className="px-2 py-2 text-slate-700 whitespace-nowrap text-left">{parseFloat(row.DisPercent || 0).toFixed(2)}</td>
                                                    <td className="px-2 py-2 text-slate-700 whitespace-nowrap text-left">₹{parseFloat(row.Discount || 0).toFixed(2)}</td>
                                                </tr>
                                            ))
                                        ) : (
                                            <tr>
                                                <td colSpan={reportConfig.table.columns.length} className="px-4 py-8 text-center text-slate-500">
                                                    No data available for the selected period
                                                </td>
                                            </tr>
                                        )}
                                    </tbody>
                                </table>
                            </div>
                        </section>
                    </div>
                </main>
            </div>
        </div>
    );
};

export default withAuth(PurchaseLedger);

